import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AuthContext } from "../../hoc/AuthContext";
import { getApiErrorMessage, jagranNextService } from "../../apiService/jagranNext.service";
import { Field, inputClass, Notice, PageShell, Panel, PrimaryButton, SecondaryButton } from "./ui";

const emptyForm = { name: "", slug: "", description: "" };

export default function CampaignForm() {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const { selectedDomain } = useContext(AuthContext);
  const productId = selectedDomain?.id;
  const isEdit = Boolean(campaignId);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!campaignId) return undefined;
    let active = true;
    async function load() {
      setLoading(true);
      try {
        const response = await jagranNextService.getCampaign(campaignId);
        if (!active) return;
        const campaign = response.data || {};
        setForm({
          name: campaign.name || "",
          slug: campaign.slug || "",
          description: campaign.description || "",
        });
      } catch (requestError) {
        if (active) setError(getApiErrorMessage(requestError));
      } finally {
        if (active) setLoading(false);
      }
    }
    load();
    return () => {
      active = false;
    };
  }, [campaignId]);

  const updateField = (key) => (event) => {
    setForm((current) => ({ ...current, [key]: event.target.value }));
  };

  const save = async (event) => {
    event.preventDefault();
    if (!isEdit && !productId) {
      setError("Select a product in the header before creating a campaign.");
      return;
    }
    setSaving(true);
    setError("");
    const body = {
      name: form.name.trim(),
      description: form.description.trim(),
    };
    if (form.slug.trim()) body.slug = form.slug.trim();
    try {
      if (isEdit) {
        await jagranNextService.updateCampaign(campaignId, body);
        navigate(`/products/jagran-next/campaigns/${campaignId}`);
      } else {
        const response = await jagranNextService.createCampaign({
          ...body,
          product_id: Number(productId),
        });
        const createdId = response.data?.id;
        navigate(createdId ? `/products/jagran-next/campaigns/${createdId}` : "/products/jagran-next");
      }
    } catch (requestError) {
      setError(getApiErrorMessage(requestError));
    } finally {
      setSaving(false);
    }
  };

  const productName =
    selectedDomain?.product_name || selectedDomain?.name || selectedDomain?.domain_url || "No product selected";

  return (
    <PageShell
      title={isEdit ? "Edit campaign" : "New campaign"}
      description={`Saved for ${productName}. Leave the slug blank to generate it from the name.`}
      actions={<SecondaryButton onClick={() => navigate("/products/jagran-next")}>Back</SecondaryButton>}
    >
      {loading ? <p className="text-sm text-gray-500">Loading campaign...</p> : null}
      {!loading ? (
        <Panel title="Campaign details" description="Name is required. Description stays inside the CMS.">
          <form onSubmit={save}>
            <Notice>{error}</Notice>
            <div className="grid gap-5 md:grid-cols-2">
              <Field label="Name" required hint="The title shown in the campaign list.">
                <input className={inputClass} value={form.name} onChange={updateField("name")} placeholder="Mutual Funds Mastery" required />
              </Field>
              <Field label="Slug" hint="Optional. Use lowercase words separated by hyphens.">
                <input className={inputClass} value={form.slug} onChange={updateField("slug")} placeholder="mutual-funds-mastery" />
              </Field>
              <Field className="md:col-span-2" label="Description">
                <textarea className={inputClass} rows={5} value={form.description} onChange={updateField("description")} placeholder="Live masterclass on mutual fund investing" />
              </Field>
            </div>
            <div className="mt-6 flex justify-end gap-3 border-t border-gray-100 pt-4">
              <SecondaryButton onClick={() => navigate("/products/jagran-next")}>Cancel</SecondaryButton>
              <PrimaryButton type="submit" disabled={saving}>{saving ? "Saving..." : "Save campaign"}</PrimaryButton>
            </div>
          </form>
        </Panel>
      ) : null}
    </PageShell>
  );
}
